/**
 * Every read the hub makes, in one place. The hub owns no API routes — it
 * composes the endpoints in CONTRACT.md. It is read-only: nothing on this screen
 * mutates state, because it can't see enough of a module's state to do it safely
 * (e.g. it can't know which chores a kid already finished today). Every card
 * links into the module that owns the action.
 */
import { api } from '../../lib/api';
import type {
  WellnessDailyStatus,
  AllowanceBalance,
  CalendarEvent,
  CategoryStatus,
  Chore,
  ChoreCompletion,
  FamilyMember,
  HealthScore,
  Identity,
  InsuranceStatusSummary,
  MealPlanEntry,
  MemberLocation,
  Notification,
  Transaction,
} from '../../shared/types';
import { parseWhen } from './dates';

// ---------------------------------------------------------------- adults only
// A child calling any of these gets a 403 by design, so the kid view never
// mounts the cards that use them.

export const fetchStatus = (period: string) =>
  api.get<CategoryStatus[]>(`/transactions/status?period=${period}`);

export const fetchHealth = (period: string) => api.get<HealthScore>(`/insights/health?period=${period}`);

/** The wellness module's whole hub payload in one call — medication, readings, food, training. */
export const fetchWellness = () => api.get<WellnessDailyStatus>('/wellness/today');

export const fetchUncategorized = (period: string) =>
  api.get<Transaction[]>(`/transactions?period=${period}&uncategorized=1`);

export const fetchFamily = () => api.get<FamilyMember[]>('/family');

// ---------------------------------------------------------------- household

export const fetchPlan = (weekStart: string) =>
  api.get<MealPlanEntry[]>(`/meals/plan?week_start=${weekStart}`);

export const fetchEvents = (from: string, to: string) =>
  api.get<CalendarEvent[]>(`/calendar?from=${from}&to=${to}`);

export const fetchLocations = () => api.get<MemberLocation[]>('/location');

export const fetchNotifications = () => api.get<Notification[]>('/notifications');

export const fetchChores = () => api.get<Chore[]>('/chores');

export const fetchAllowance = (memberId: number) =>
  api.get<AllowanceBalance>(`/chores/allowance?member_id=${memberId}`);

/**
 * Chore completions waiting on an adult's approval — the hub's whole reason for
 * existing ("what needs me?").
 *
 * No try/catch: a failed read is NOT "nothing waiting". Swallowing the failure
 * would tell an adult the house is clear while a kid's allowance sits unpaid, so
 * the error propagates to the card, which says so and offers a retry.
 */
export const fetchPendingApprovals = () =>
  api.get<ChoreCompletion[]>('/chores/completions?status=pending');

/** Adults only — the hub gates the call, and the Worker gates it again. */
export const fetchInsuranceStatus = () =>
  api.get<InsuranceStatusSummary>('/insurance/status');

// ---------------------------------------------------------------- selectors

/** Tonight's dinner from this week's plan. */
export function dinnerFor(plan: MealPlanEntry[] | null, date: string): MealPlanEntry | null {
  return plan?.find((e) => e.plan_date === date && e.slot === 'dinner') ?? null;
}

/** The next few things on the calendar, soonest first, nothing already finished. */
export function upcoming(events: CalendarEvent[] | null, limit: number): CalendarEvent[] {
  if (!events) return [];
  const now = Date.now();
  return [...events]
    .filter((e) => {
      // parseWhen(), not new Date(): D1 stores UTC with no zone marker, so a raw
      // parse reads it as local and a finished event lingers here for hours.
      const endMs = parseWhen(e.ends_at ?? e.starts_at).getTime();
      // All-day events count for the whole day; timed ones drop off once they end.
      return e.all_day || Number.isNaN(endMs) || endMs >= now;
    })
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
    .slice(0, limit);
}

export function unreadCount(notes: Notification[] | null): number {
  return notes?.filter((n) => !n.read_at).length ?? 0;
}

/** A kid's chores: assigned to them, or up for grabs. */
export function choresFor(chores: Chore[] | null, memberId: number | null): Chore[] {
  if (!chores) return [];
  return chores.filter((c) => c.active && (c.assigned_to === memberId || c.assigned_to === null));
}

export function memberById(family: FamilyMember[] | null, id: number | null): FamilyMember | null {
  if (!family || id === null) return null;
  return family.find((m) => m.id === id) ?? null;
}

/**
 * The member's own color (family_members.color — never hardcoded).
 *
 * `Identity.color` is the caller's own row from D1 and is present for adults and
 * children alike, so this works on the kid view, which cannot read the
 * adult-gated roster. The roster is only consulted as a freshness check for
 * adults, who already have it loaded.
 */
export function colorFor(identity: Identity, family: FamilyMember[] | null): string | null {
  return memberById(family, identity.memberId)?.color ?? identity.color;
}

/** Illustrative bands for the health score. Never advice. */
export function band(score: number): { label: string; tone: 'ok' | 'near' | 'over' } {
  if (score >= 80) return { label: 'Strong', tone: 'ok' };
  if (score >= 60) return { label: 'Steady', tone: 'ok' };
  if (score >= 40) return { label: 'Watch it', tone: 'near' };
  return { label: 'Off track', tone: 'over' };
}
